import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTable, useSortBy, useGlobalFilter, usePagination } from "react-table";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

/** ---------- Simple Toast System (no external libs) ---------- */
function ToastStack({ toasts, onRemove }) {
  const stackRef = useRef(null);

  useEffect(() => {
    if (!stackRef.current) return;
    gsap.fromTo(
      stackRef.current.querySelectorAll(".toast-item"),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, ease: "power2.out" }
    );
  }, [toasts]);

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div ref={stackRef} className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-[#b29063]/30 bg-[#f9f7f3] text-[#a08053]";

          return (
            <div
              key={t.id}
              className={[
                "toast-item pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border bg-white/60 px-2 py-1 text-xs font-semibold hover:bg-white"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** ---------- Confirm Modal (no window.confirm) ---------- */
function ConfirmModal({ open, title, description, confirmText, cancelText, onConfirm, onCancel }) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open || !modalRef.current) return;
    gsap.fromTo(
      modalRef.current,
      { opacity: 0, y: 12, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.22, ease: "power2.out" }
    );
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div ref={modalRef} className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="border-b px-5 py-4">
          <div className="text-lg font-semibold text-gray-900">{title}</div>
          {description && <div className="mt-1 text-sm text-gray-600">{description}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            {cancelText || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-xl bg-[#b29063] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a08053] active:translate-y-[1px]"
          >
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // User detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // Confirm modal for actions
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    action: null, // function to run on confirm
  });

  // Toasts
  const [toasts, setToasts] = useState([]);

  // GSAP refs
  const wrapRef = useRef(null);
  const rowsRef = useRef(null);
  const userModalRef = useRef(null);

  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, message, title }]);
    // auto-dismiss
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2800);
  };

  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const openConfirm = ({ title, description, confirmText, cancelText, action }) => {
    setConfirmState({ open: true, title, description, confirmText, cancelText, action });
  };

  const closeConfirm = () => {
    setConfirmState((s) => ({ ...s, open: false, action: null }));
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      toast("info", "Loading users...");

      const res = await fetch(`${ApiURl}/getUsers.php`);
      const data = await res.json();

      const list = Array.isArray(data) ? data : data?.data || [];
      setUsers(Array.isArray(list) ? list : []);

      toast("success", `Loaded ${Array.isArray(list) ? list.length : 0} users.`);
    } catch (err) {
      console.error(err);
      setUsers([]);
      toast("error", "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GSAP: page entrance
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".ul-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);
    return () => ctx.revert();
  }, []);

  // GSAP: rows fade-in whenever users changes
  useLayoutEffect(() => {
    if (!rowsRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".ul-row",
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 0.25, stagger: 0.02, ease: "power2.out" }
      );
    }, rowsRef);
    return () => ctx.revert();
  }, [users]);

  // GSAP: user detail modal pop-in
  useEffect(() => {
    if (!isUserModalOpen || !userModalRef.current) return;
    gsap.fromTo(
      userModalRef.current,
      { opacity: 0, y: 12, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.22, ease: "power2.out" }
    );
  }, [isUserModalOpen]);

  // Open/close user modal
  const openUserModal = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setSelectedUser(null);
  };

  // Toggle status (active/inactive) with confirm + optimistic update
  const requestToggleStatus = (user) => {
    const current = user.status;
    const nextStatus = current === "active" ? "inactive" : "active";

    openConfirm({
      title: "Confirm status change",
      description: `Mark "${user.username}" as ${nextStatus}?`,
      confirmText: `Set ${nextStatus}`,
      cancelText: "Cancel",
      action: async () => {
        closeConfirm();

        // optimistic update
        setUsers((prev) =>
          prev.map((u) => (u.user_id === user.user_id ? { ...u, status: nextStatus } : u))
        );

        try {
          const res = await fetch(`${ApiURl}/deleteUser.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: user.user_id, status: nextStatus }),
          });

          const result = await res.json().catch(() => ({}));
          if (!result?.success) throw new Error(result?.message || "Update failed");

          toast("success", result.message || `User marked as ${nextStatus}.`);
        } catch (err) {
          console.error(err);
          // rollback
          setUsers((prev) =>
            prev.map((u) => (u.user_id === user.user_id ? { ...u, status: current } : u))
          );
          toast("error", "Failed to update user status.");
        }
      },
    });
  };

  // Toggle role (admin/user) with confirm + optimistic update
  const requestToggleRole = (user) => {
    const currentRole = user.role;
    const nextRole = currentRole === "admin" ? "user" : "admin";

    openConfirm({
      title: "Confirm role change",
      description: `Make "${user.username}" a ${nextRole}?`,
      confirmText: `Make ${nextRole}`,
      cancelText: "Cancel",
      action: async () => {
        closeConfirm();

        // optimistic update
        setUsers((prev) =>
          prev.map((u) => (u.user_id === user.user_id ? { ...u, role: nextRole } : u))
        );

        try {
          const res = await fetch(`${ApiURl}/updateUserRole.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: user.user_id, role: nextRole }),
          });

          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          const result = await res.json().catch(() => ({}));
          if (!result?.success) throw new Error(result?.message || "Role update failed");

          toast("success", result.message || `Role updated to ${nextRole}.`);
        } catch (err) {
          console.error(err);
          // rollback
          setUsers((prev) =>
            prev.map((u) => (u.user_id === user.user_id ? { ...u, role: currentRole } : u))
          );
          toast("error", "Failed to update user role.");
        }
      },
    });
  };

  const data = useMemo(() => users, [users]);

  const columns = useMemo(
    () => [
      {
        Header: "User ID",
        accessor: "user_id",
        Cell: ({ value }) => <span className="font-semibold text-gray-900">{value}</span>,
      },
      {
        Header: "Name",
        accessor: "username",
        Cell: ({ value }) => <span className="font-medium text-gray-900">{value || "-"}</span>,
      },
      {
        Header: "Phone",
        accessor: "phone",
        Cell: ({ value }) => <span className="text-gray-700">{value || "-"}</span>,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => <span className="text-gray-700">{value || "-"}</span>,
      },
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ value }) => (
          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold text-gray-700">
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        disableSortBy: true,
        Cell: ({ row }) => {
          const isActive = row.original.status === "active";
          return (
            <button
              type="button"
              onClick={() => requestToggleStatus(row.original)}
              className={[
                "relative inline-flex h-6 w-11 items-center rounded-full transition",
                isActive ? "bg-green-500" : "bg-gray-300",
              ].join(" ")}
              aria-label="Toggle status"
              title="Toggle status"
            >
              <span
                className={[
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow transition",
                  isActive ? "translate-x-5" : "translate-x-1",
                ].join(" ")}
              />
            </button>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openUserModal(row.original)}
              className="rounded-lg border px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              View
            </button>

            <button
              type="button"
              onClick={() => requestToggleRole(row.original)}
              className="rounded-lg bg-[#b29063] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#a08053] active:translate-y-[1px]"
            >
              {row.original.role === "admin" ? "Make User" : "Make Admin"}
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    pageOptions,
    gotoPage,
    setPageSize,
  } = tableInstance;

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <div ref={wrapRef} className="w-full px-4">
      {/* Toasts */}
      <ToastStack toasts={toasts} onRemove={removeToast} />

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onCancel={closeConfirm}
        onConfirm={async () => {
          if (typeof confirmState.action === "function") await confirmState.action();
        }}
      />

      {/* Header / Controls */}
      <div className="ul-anim mb-4 rounded-2xl border bg-white p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Users</h2>
            <p className="mt-1 text-sm text-gray-600">
              Search, sort, manage roles, and activate/deactivate users.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search users..."
              className="w-full sm:w-72 rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
            />

            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
            >
              {[10, 20, 30, 50].map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={fetchUserData}
              className="rounded-xl bg-[#b29063] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a08053] active:translate-y-[1px]"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="ul-anim rounded-2xl border bg-white overflow-hidden">
        {/* Mobile-specific scrolling container */}
        <div className="block md:hidden">
          <div 
            className="overflow-x-auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              width: '100%'
            }}
          >
            <div style={{ minWidth: '1050px' }}>
              <table {...getTableProps()} className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  {headerGroups.map((headerGroup, i) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                      {headerGroup.headers.map((column, j) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          key={j}
                          className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-600"
                        >
                          <div className="flex items-center gap-2">
                            {column.render("Header")}
                            {column.canSort && (
                              <span className="text-gray-400">
                                {column.isSorted ? (column.isSortedDesc ? "▼" : "▲") : "↕"}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()} ref={rowsRef}>
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-600">
                        <div className="flex justify-center">
                          <div className="spinner border-4 border-gray-200 border-t-[#b29063] rounded-full w-8 h-8 animate-spin"></div>
                        </div>
                      </td>
                    </tr>
                  ) : page.length > 0 ? (
                    page.map((row, idx) => {
                      prepareRow(row);
                      return (
                        <tr
                          {...row.getRowProps()}
                          key={row.original?.user_id ?? idx}
                          className="ul-row border-t hover:bg-gray-50"
                        >
                          {row.cells.map((cell, cidx) => (
                            <td
                              {...cell.getCellProps()}
                              key={cidx}
                              className="whitespace-nowrap px-4 py-3 text-gray-800"
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="border-t">
                      <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-600">
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-2xl">😕</span>
                          <p>No users found. Try a different search or refresh the page.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Desktop view */}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                    {headerGroup.headers.map((column, j) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={j}
                        className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-600"
                      >
                        <div className="flex items-center gap-2">
                          {column.render("Header")}
                          {column.canSort && (
                            <span className="text-gray-400">
                              {column.isSorted ? (column.isSortedDesc ? "▼" : "▲") : "↕"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()} ref={rowsRef}>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-600">
                      <div className="flex justify-center">
                        <div className="spinner border-4 border-gray-200 border-t-[#b29063] rounded-full w-8 h-8 animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                ) : page.length > 0 ? (
                  page.map((row, idx) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        key={row.original?.user_id ?? idx}
                        className="ul-row border-t hover:bg-gray-50"
                      >
                        {row.cells.map((cell, cidx) => (
                          <td
                            {...cell.getCellProps()}
                            key={cidx}
                            className="whitespace-nowrap px-4 py-3 text-gray-800"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr className="border-t">
                    <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-600">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-2xl">😕</span>
                        <p>No users found. Try a different search or refresh the page.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600">
            Page <span className="font-semibold">{pageIndex + 1}</span> of{" "}
            <span className="font-semibold">{pageOptions.length}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              className="rounded-xl border px-3 py-2 text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              First
            </button>
            <button
              type="button"
              onClick={previousPage}
              disabled={!canPreviousPage}
              className="rounded-xl border px-3 py-2 text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Prev
            </button>
            <span className="px-3 py-2 text-sm">
              {pageIndex + 1}
            </span>
            <button
              type="button"
              onClick={nextPage}
              disabled={!canNextPage}
              className="rounded-xl border px-3 py-2 text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => gotoPage(pageOptions.length - 1)}
              disabled={!canNextPage}
              className="rounded-xl border px-3 py-2 text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {isUserModalOpen && selectedUser && (
        <div
          className="fixed inset-0 z-[9997] flex items-center justify-center bg-black/40 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeUserModal();
          }}
        >
          <div ref={userModalRef} className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <div className="text-lg font-semibold text-gray-900">User Details</div>
                <div className="text-sm text-gray-600">ID: {selectedUser.user_id}</div>
              </div>
              <button
                onClick={closeUserModal}
                className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
                type="button"
              >
                Close
              </button>
            </div>

            <div className="space-y-3 px-5 py-4 text-sm">
              <div className="flex justify-between gap-6">
                <span className="text-gray-600">Name</span>
                <span className="font-semibold text-gray-900">{selectedUser.username || "-"}</span>
              </div>
              <div className="flex justify-between gap-6">
                <span className="text-gray-600">Email</span>
                <span className="font-semibold text-gray-900">{selectedUser.email || "-"}</span>
              </div>
              <div className="flex justify-between gap-6">
                <span className="text-gray-600">Phone</span>
                <span className="font-semibold text-gray-900">{selectedUser.phone || "-"}</span>
              </div>
              <div className="flex justify-between gap-6">
                <span className="text-gray-600">Role</span>
                <span className="font-semibold text-gray-900">{selectedUser.role || "-"}</span>
              </div>
              <div className="flex justify-between gap-6">
                <span className="text-gray-600">Status</span>
                <span className="font-semibold text-gray-900">{selectedUser.status || "-"}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => requestToggleStatus(selectedUser)}
                className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              >
                Toggle Status
              </button>
              <button
                type="button"
                onClick={() => requestToggleRole(selectedUser)}
                className="rounded-xl bg-[#b29063] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a08053] active:translate-y-[1px]"
              >
                Toggle Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;